import { useCallback } from "react";

import { useForm, zodResolver } from "@mantine/form";

import { useTimerContext } from "@/components/Timer/TimerContext";
import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { RomFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/components/RomFormFields";
import {
  ROM_DATE_LINK_ID,
  ROM_DATE_TIME_LINK_ID,
  ROM_QUESTIONNAIRE_NAME,
  ROM_ROOT_LINK_ID,
  ROM_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/constants/rom-link-ids";
import type { RomFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/types/rom-drawer";
import type { RomFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/types/rom-form";
import {
  getDefaultRomFlowsheetFormValues,
  normalizeRomFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/utils/rom-form-defaults";
import {
  mapQuestionnaireResponseToRomFormValues,
  mapRomFormValuesToQuestionnaireValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/utils/rom-questionnaire-mapping";
import { romFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/zod-schemas/rom-flowsheet-form";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import {
  type LaborFlowsheetDrawerEditContext,
  useLaborFlowsheetDrawerPersist,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";
import { useBirthMilestonesContext } from "@/encounters/contexts/BirthMilestonesProvider";

const ROM_DISPLAY_NAME = LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.RuptureOfMembranes];

/** Builds edit-mode persist context from drawer props; undefined for quick-add. */
function getRomEditContext(
  props: RomFlowsheetDrawerProps,
): LaborFlowsheetDrawerEditContext | undefined {
  if (props.mode !== "edit") {
    return undefined;
  }

  return {
    observation: props.observation,
    questionnaireDisplayName: props.questionnaireDisplayName,
  };
}

export function mergeRomHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: RomFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): RomFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[ROM_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[ROM_DATE_TIME_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedExamDate = hydratedDateTime?.[ROM_DATE_LINK_ID];
  const hydratedExamTime = hydratedDateTime?.[ROM_TIME_LINK_ID];

  return {
    ...mapped,
    examDate: hydratedExamDate instanceof Date ? hydratedExamDate : mapped.examDate,
    examTime: typeof hydratedExamTime === "string" ? hydratedExamTime : mapped.examTime,
  };
}

export function RomFlowsheetDrawer(props: RomFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;
  const { setupRomMilestone } = useBirthMilestonesContext();
  const { resetTimer } = useTimerContext();

  const form = useForm<RomFlowsheetFormValues>({
    initialValues: getDefaultRomFlowsheetFormValues(),
    validate: zodResolver(romFlowsheetFormSchema),
  });

  const runQuickAddLocalSideEffects = useCallback(
    async ({ normalizedValues }: { normalizedValues: Record<string, unknown> }) => {
      const romRoot = normalizedValues[ROM_ROOT_LINK_ID] as Record<string, unknown> | undefined;
      const romDateTimeGroup = romRoot?.[ROM_DATE_TIME_LINK_ID] as
        | { romDate: string; romTime: string }
        | undefined;

      if (romDateTimeGroup?.romDate && romDateTimeGroup?.romTime) {
        setupRomMilestone({
          rom: `${romDateTimeGroup.romDate}T${romDateTimeGroup.romTime}Z`,
        });
        resetTimer("ROM");
      }
    },
    [resetTimer, setupRomMilestone],
  );

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<RomFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: ROM_QUESTIONNAIRE_NAME,
    displayName: ROM_DISPLAY_NAME,
    timerKey: "ROM",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeRomFlowsheetFormValues(romFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultRomFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapRomFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToRomFormValues,
    mergeHydratedEditFormValues: mergeRomHydratedEditFormValues,
    questionnaireNotFoundMessage: "ROM questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing ROM entry.",
    editContext: getRomEditContext(props),
    onRefresh: props.mode === "quick-add" ? props.onRefresh : undefined,
    runQuickAddLocalSideEffects:
      props.mode === "quick-add" ? runQuickAddLocalSideEffects : undefined,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      submitButtonText={mode === "edit" ? "Update" : "Add To Flowsheet"}
      formId={`labor-flowsheet-form-${QuestionnaireType.RuptureOfMembranes}`}
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <RomFormFields form={form} mode={mode} clientId={clientId} isDrawerOpened={opened} />
    </LaborFlowsheetDrawerShell>
  );
}
